import React from 'react';
import {Navigate, useRoutes} from 'react-router-dom';
import {authRole, RoutePermittedRole} from 'shared/constants/AppConst';
import {checkPermission} from './helper/RouteHelper';

/**
 * Terminal "access denied" for an authenticated user hitting a route their role
 * is not permitted on. MUST NOT navigate: redirecting to a fallback that itself
 * role-redirects back produces an infinite <Navigate> loop ("Maximum update depth
 * exceeded"). Rendering a stable leaf breaks the cycle. (This is what admin hit
 * when redirected onto the superadmin-only /platform/overview.)
 */
const RoleAccessDenied = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 24,
      gap: 8,
    }}
  >
    <h2 style={{margin: 0}}>Access Denied</h2>
    <p style={{color: '#6b7280', maxWidth: 420}}>
      You don&apos;t have permission to view this page. Use the menu to open a
      section available to your role.
    </p>
  </div>
);

/**
 * Build the plain routes array (NO hooks here)
 */
export function buildRoutes(structure = {}) {
  const {
    isAuthenticated = false,
    anonymousStructure = {},
    authorizedStructure = {},
    unAuthorizedStructure = {},
    userRole = authRole.user, // must be an array if you use .indexOf elsewhere
  } = structure;

  const dynamicRoutes = [];

  if (anonymousStructure?.routes) {
    dynamicRoutes.push(
      ...routesGenerator(isAuthenticated, anonymousStructure, 'anonymous'),
    );
  }
  if (authorizedStructure?.routes) {
    dynamicRoutes.push(
      ...routesGenerator(
        isAuthenticated,
        authorizedStructure,
        'authorized',
        isAuthenticated ? userRole : null,
      ),
    );
  }
  if (unAuthorizedStructure?.routes) {
    dynamicRoutes.push(
      ...routesGenerator(
        isAuthenticated,
        unAuthorizedStructure,
        'unAuthorized',
      ),
    );
  }

  return dynamicRoutes;
}

/**
 * Generate route records without hooks
 */
function routesGenerator(
  isAuthenticated,
  routeSet = {},
  type = 'anonymous',
  userRole,
) {
  const generatedRoutes = [];
  const {fallbackPath = '', unAuthorizedComponent} = routeSet;

  const isAnonymous = type === 'anonymous';
  const isAuthorized = type === 'authorized';

  const addRoute = (record) => generatedRoutes.push(record);

  for (const route of routeSet.routes || []) {
    const {
      path = '',
      element,
      permittedRole = RoutePermittedRole.user,
      redirectPath = '',
      showRouteIf = true,
    } = route || {};

    if (!showRouteIf || !path) continue;

    // anonymous bucket => always add
    if (isAnonymous) {
      addRoute(route);
      continue;
    }

    const renderCondition = isAuthorized ? isAuthenticated : !isAuthenticated;

    const makeElem = () => {
      if (!renderCondition) {
        return <Navigate to={redirectPath || fallbackPath} replace />;
      }
      // if authorized bucket, also check role. A role mismatch renders a
      // terminal leaf (NOT <Navigate>) to avoid redirect loops (see above).
      if (isAuthorized) {
        const allowed = checkPermission(permittedRole, userRole);
        if (!allowed) return unAuthorizedComponent || <RoleAccessDenied />;
      }
      return element;
    };

    if (Array.isArray(path)) {
      path.forEach((p) =>
        addRoute({path: p, element: makeElem(), permittedRole}),
      );
    } else {
      addRoute({path, element: makeElem(), permittedRole});
    }
  }

  return generatedRoutes;
}

/**
 * Component that safely calls useRoutes
 */
export default function RouteGenerator(structure) {
  const routes = React.useMemo(() => buildRoutes(structure), [structure]);
  return useRoutes(routes);
}
